import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import QuizContent from '../shared/QuizContent';
import QuizTitle from '../shared/QuizTitle';
import QuizOptions from '../shared/QuizOptions';
import QuizOption from '../shared/QuizOption';
import { getQuizOptionsData } from '../../constants';
import { useVideoAnimation } from '../../hooks/useVideoAnimation';
import {
  ethnicityKey,
  useGrowthBookValues,
} from '../../contexts/GrowthBookContext';

// Step 3 reuses Step 1's (create-friend Step 0) layout — same two-card video
// reveal — for Slim / Athletic. The selected body type drives which character
// gets revealed in the Summary step.
const Step3 = ({ onAnswer, onPrevious, selections, isTransitioning }) => {
  const { animationMode, getVideoProps } = useVideoAnimation();
  const { videos: gbVideos } = useGrowthBookValues();
  const { t } = useTranslation();

  const bodyTypes = getQuizOptionsData('bodyType');

  // GB override (branched by Step 1 ethnicity) > hardcoded constant.
  const eKey = ethnicityKey(selections?.step1?.ethnicity);
  const resolveVideoSrc = (option) =>
    gbVideos?.step3?.[eKey]?.[option.value] || option.video;

  const [selectedOption, setSelectedOption] = useState(
    selections?.step3?.bodyType || bodyTypes[0]?.value || '',
  );
  const videoRefs = useRef([]);
  const [videosReady, setVideosReady] = useState(false);

  const handleOptionClick = useCallback(
    (value) => {
      if (isTransitioning) return;
      setSelectedOption(value);
    },
    [isTransitioning],
  );

  useEffect(() => {
    const cleanups = [];

    videoRefs.current.forEach((videoRef) => {
      if (!videoRef) return;

      videoRef.muted = true;
      videoRef.loop = true;
      videoRef.playsInline = true;
      videoRef.setAttribute('playsinline', '');
      videoRef.setAttribute('webkit-playsinline', '');
      videoRef.preload = 'auto';
      videoRef.controls = false;
      videoRef.removeAttribute('controls');
      videoRef.removeAttribute('controlsList');

      const tryPlay = () => {
        if (videoRef.paused) videoRef.play().catch(() => {});
      };

      videoRef.addEventListener('loadedmetadata', tryPlay);
      videoRef.addEventListener('canplay', tryPlay);
      videoRef.addEventListener('loadeddata', tryPlay);

      if (videoRef.readyState >= 3) tryPlay();

      cleanups.push(() => {
        videoRef.removeEventListener('loadedmetadata', tryPlay);
        videoRef.removeEventListener('canplay', tryPlay);
        videoRef.removeEventListener('loadeddata', tryPlay);
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, [animationMode, bodyTypes]);

  const playVideo = useCallback((index) => {
    const video = videoRefs.current[index];
    if (video && video.paused) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!videosReady || !selectedOption) return;
    if (animationMode !== 'AUTOMATIC') return;

    const idx = bodyTypes.findIndex((opt) => opt.value === selectedOption);
    if (idx === -1) return;

    const timer = setTimeout(() => playVideo(idx), 100);
    return () => clearTimeout(timer);
  }, [videosReady, selectedOption, bodyTypes, playVideo, animationMode]);

  const handleVideoCanPlay = useCallback((index) => {
    if (index === 0) setVideosReady(true);
    const video = videoRefs.current[index];
    if (video && video.paused) video.play().catch(() => {});
  }, []);

  // Three-layer watchdog (visibility, per-video pause, setInterval) — same
  // pattern as Step 1 to keep videos playing on iOS Safari.
  useEffect(() => {
    let resumeTimer = null;
    const lastTimes = {};

    const forcePlay = (video, retriesLeft = 3) => {
      video.currentTime = 0;
      video.play().catch(() => {
        if (retriesLeft > 0) {
          setTimeout(() => forcePlay(video, retriesLeft - 1), 500);
        }
      });
    };

    const playAll = () => {
      videoRefs.current.forEach((video) => {
        if (video) forcePlay(video);
      });
    };

    const schedulePlayAll = () => {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(playAll, 200);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') schedulePlayAll();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', schedulePlayAll);

    const pauseCleanups = [];
    videoRefs.current.forEach((video) => {
      if (!video) return;
      const onPause = () => {
        if (document.visibilityState === 'hidden') schedulePlayAll();
      };
      video.addEventListener('pause', onPause);
      pauseCleanups.push(() => video.removeEventListener('pause', onPause));
    });

    const watchdog = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      videoRefs.current.forEach((video, i) => {
        if (!video) return;
        if (video.paused) {
          forcePlay(video);
        } else if (video.currentTime === lastTimes[i]) {
          forcePlay(video);
        }
        lastTimes[i] = video.currentTime;
      });
    }, 1000);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', schedulePlayAll);
      pauseCleanups.forEach((fn) => fn());
      clearInterval(watchdog);
      if (resumeTimer) clearTimeout(resumeTimer);
    };
  }, []);

  const handleContinue = useCallback(() => {
    if (isTransitioning || !selectedOption) return;
    onAnswer('step3', { bodyType: selectedOption });
  }, [selectedOption, onAnswer, isTransitioning]);

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return;
    onPrevious();
  }, [onPrevious, isTransitioning]);

  return (
    <div
      className={`quizContentWrapper quizContentWrapperStep0 ${
        isTransitioning ? 'no-click' : ''
      }`}
    >
      <QuizContent className="mainStep">
        <QuizTitle>
          {t('choose')} <span>{t('body_text')}</span>
        </QuizTitle>
        <div className="step0Wrapper">
          <QuizOptions className="step0-options">
            {bodyTypes.map((option, index) => {
              const isSelected = selectedOption === option.value;
              return (
                <QuizOption
                  key={option.value}
                  value={option.value}
                  name="bodyType"
                  onClick={() => handleOptionClick(option.value)}
                  className={`step0-option ${
                    isSelected
                      ? 'selected'
                      : selectedOption !== null
                      ? 'not-selected'
                      : ''
                  }`}
                >
                  <div className="option-image">
                    <video
                      ref={(el) => (videoRefs.current[index] = el)}
                      className="step0Video"
                      {...getVideoProps()}
                      src={resolveVideoSrc(option)}
                      preload="auto"
                      controls={false}
                      controlsList="nodownload nofullscreen noremoteplayback"
                      playsInline
                      webkit-playsinline=""
                      onCanPlay={() => handleVideoCanPlay(index)}
                    />
                    <div className="step0-image-title">{t(option.text)}</div>
                  </div>
                </QuizOption>
              );
            })}
          </QuizOptions>

          <div className="navigation-wrapper">
            <button
              className="previous-button"
              onClick={handlePrevious}
              disabled={isTransitioning}
            >
              {t('previous')}
            </button>
            <button
              className="continue-button"
              onClick={handleContinue}
              disabled={!selectedOption || isTransitioning}
            >
              {t('next')}
            </button>
          </div>
        </div>
      </QuizContent>
    </div>
  );
};

export default Step3;
